import {
  Row,
  Col,
  Form,
  Image,
  Button,
  Tooltip,
  Accordion,
  OverlayTrigger
} from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import styled from "styled-components";
import { useSelector } from "react-redux";
import {
  faEye,
  faClone,
  faTrash,
  faUpload,
  faArrowsUpDown,
  faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import Spinner from "react-bootstrap/Spinner";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams, useLocation } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  THUMBNAIL_TYPE,
  DEFAULT_WEB_TEMP,
  DNS_RECORD_TYPES,
  DEFAULT_WEBSITE_LANGUAGE,
  WEBSITE_LANGUAGE_OPTIONS
} from "../../strings";
import {
  Legend,
  FieldSet,
  CustomCard,
  PageHeading
} from "../../common-styling";
import {
  getDnsRecord,
  updateCustomer,
  getCustomerById,
  deleteTemplateDraft,
  generateWebsiteImages,
  generateWebsiteContent,
  saveCustomerWebTemplate,
  getCustomerAllWebTemplates,
  getWebsiteDomainByCustomerId,
  getCustomerActiveWebsiteTemplate
} from "../../../services/api-declaration";
import {
  getDefaultWebsiteEditorValues,
  getInitialWebsiteEditorValues
} from "../../../services/customer-website-helper";
import { useDialog } from "../../../hooks/useDialog";
import MapSection from "../website-sections/map-section";
import FaqSection from "../website-sections/faq-section";
import { useTabRoute } from "../../../hooks/useTabRoute";
import FirstBanner from "../website-sections/first-banner";
import HeroSection from "../website-sections/hero-section";
import AboutSection from "../website-sections/about-section";
import SecondBanner from "../website-sections/second-banner";
import PreventReload from "../../funnel-form/prevent-reload";
import AccordionBody from "react-bootstrap/esm/AccordionBody";
import HeaderSection from "../website-sections/header-section";
import FooterSection from "../website-sections/footer-section";
import AddNewSection from "../website-sections/add-new-section";
import ServiceSection from "../website-sections/service-section";
import KeyPointSection from "../website-sections/key-point-section";
import { websiteSchema } from "../website-schema/validation-schema";
import ImagePreviewModal from "../../funnel-form/preview-image-modal";
import { showError, showSuccess } from "../../../services/toast-service";
import BrandColorsSection from "../website-sections/brand-colors-section";
import { generateCustomerSitePreviewUrl } from "../../../services/common-service";
import WebsiteDomainSection from "../../reusable-form-section/webiste-domain-section";
import { useTranslation } from "react-i18next";

const WebsiteInfo = styled.div`
  background: var(--body-light-bg);
  border-bottom: 3px solid var(--primary-color);
`;
const Img = styled(Image)`
  height: 300px;
  width: 150px;
  object-fit: cover;
`;

const CustomerWebsite = () => {
  const startPosRef = useRef({ x: 0, y: 0 });
  const threshold = 5;
  const allowClickRef = useRef(true); // Prevent accordion toggle on drag
  const params = useParams();
  const { t } = useTranslation();
  const { tabRoute } = useTabRoute();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmittingForce, setIsSubmittingForce] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [isDomainValid, setIsDomainValid] = useState(true);
  const [activeTemplate, setActiveTemplate] = useState({});
  const [templates, setTemplates] = useState([]);
  const [hoverActive, setHoverActive] = useState(-1);
  const [selectedTemplate, setSelectedTemplate] = useState(DEFAULT_WEB_TEMP);
  const [showPreviewTemplate, setShowPreviewTemplate] = useState(-1);
  const [mainLinks, setMainLinks] = useState([]);
  const [isAllowedChange, setIsAllowedChange] = useState(true);
  const [sectionActiveKeys, setSectionActiveKeys] = useState([
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7"
  ]);
  const [isDraftWarning, setIsDraftWarning] = useState(false);
  const [isConflictWarning, setIsConflictWarning] = useState(false);
  const [dnsResult, setDnsResult] = useState(null);
  const { state } = useLocation();
  const [dnsLoading, setDnsLoading] = useState(false);
  const [showCheckDNS, setShowCheckDNS] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [category, setCategory] = useState(null);
  const [customer, setCustomer] = useState(state?.customer);
  const isHubspotTabSelected = state?.isHubspotTabSelected;

  // const [isGeneratingContent, setIsGeneratingContent] = useState(false);

  const oca = useRef(0);

  const isFormChanged = useRef(false);
  const isFormResetting = useRef(false);
  const apiIntervalHandler = useRef(undefined);

  const { REACT_APP_ASSET_CDN_URL } = process.env;

  const {
    showDuplicateWebTemplateDialogBox,
    showAIPromptDialogBox,
    showConfirmationDialogBox,
    setDependentStates
  } = useDialog();
  const tags = useSelector((state) => state.tags);
  function extractImageRequirements(sections) {
    const result = {};
    sections.forEach((section) => {
      const { component, data, name } = section;
      if (!component || !data) return;
      let imageCount = 0;
      imageCount = countImages(data);
      if (imageCount > 0) {
        const matchedSystemIdentifier = tags.tags.find(
          (item) => item.systemIdentifier === component
        );
        const systemTagId = matchedSystemIdentifier
          ? matchedSystemIdentifier._id
          : null;
        result[name] = {
          imagesRequired: imageCount,
          component: component,
          name: name,
          systemTagId: systemTagId
        };
      }
    });
    return result;
  }
  // Helper function to count images for each section
  function countImages(obj) {
    if (!obj || typeof obj !== "object") return 0;
    let count = 0;
    if (
      (obj.image && typeof obj.image === "object") ||
      (obj.bg_image && typeof obj.bg_image === "object")
    ) {
      count += 1;
    }
    if (Array.isArray(obj.images)) {
      count += obj.images.length;
    }
    if (Array.isArray(obj.services)) {
      count += obj.services.filter((service) => service.image).length;
    }
    return count;
  }
  const toggleConfirmationDialog = () => {
    showConfirmationDialogBox({
      dialogMessage: renderDeleteDialogMessage(),
      dialogType: "danger",
      responseAction: deleteDialogResponseAction,
      title: t("customerWebsite.deleteSection")
    });
  };

  const toggleForcePushDialog = () => {
    showConfirmationDialogBox({
      dialogMessage: renderForcePushDialogMessage(),
      dialogType: "danger",
      responseAction: forcePushResponseAction,
      title: "Merge Conflict",
      acceptLabel: "Forcely Merge",
      declineLabel: "Reload"
    });
  };

  const deleteDialogResponseAction = (action, values) => {
    if (action) {
      removeSection(values.index);
    }
  };

  const renderDeleteDialogMessage = () => (
    <div
      dangerouslySetInnerHTML={{
        __html: t("customerWebsite.deleteSectionMessage")
      }}
    ></div>
  );

  const renderForcePushDialogMessage = () => (
    <div
      dangerouslySetInnerHTML={{
        __html: "Someone is working on the same template"
      }}
    ></div>
  );

  const logoTypes = [
    { name: t("customerWebsite.text"), _id: "text" },
    { name: t("customerWebsite.image"), _id: "image" }
  ];

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    move(source.index, destination.index);
    isFormChanged.current = true;
  };

  const handleCollapseSections = () => {
    setSectionActiveKeys([]);
    if (sectionActiveKeys.length > 0) {
      setSectionActiveKeys([]);
    } else {
      const sectionKeys = sections.map((_, index) => index.toString());
      setSectionActiveKeys(sectionKeys);
    }
  };

  const toggleDuplicateDialog = () => {
    showDuplicateWebTemplateDialogBox({
      customerId: params.id,
      tabRoute: tabRoute,
      formValues: getValues()
    });
  };

  const toggleAIPromptDialog = () => {
    showAIPromptDialogBox({
      customerId: params.id,
      customerContactData: {
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone,
        address: customer
          ? `${customer.address || ""}, ${customer.zipCode || ""} ${
              customer.city?.name
                ? customer.city.name
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                : ""
            }`.trim()
          : "",
        email: customer.email
      },
      generateContent: generateContent
    });
  };

  const initialValues = useMemo(() => getInitialWebsiteEditorValues(), []);

  const defaultValues = useMemo(
    () => getDefaultWebsiteEditorValues(getInitialWebsiteEditorValues(), t),
    [t]
  );

  const methods = useForm({
    resolver: yupResolver(websiteSchema(logoTypes)),
    defaultValues
  });

  const {
    formState: { errors, isDirty },
    reset,
    getValues,
    watch,
    setValue,
    handleSubmit,
    register,
    trigger,
    control
  } = methods;

  const {
    fields: sections,
    append: addSection,
    remove: removeSection,
    replace: replaceSection,
    move
  } = useFieldArray({
    control,
    name: "sections"
  });

  const forcePushResponseAction = async (action) => {
    if (action) {
      setIsSubmittingForce(true);
      const response = await getCustomerActiveWebsiteTemplate({
        customerId: params.id,
        tabRoute
      });

      if (response.status === 200) {
        const template = response.data.data;
        oca.current = template?.oca;
      }
      await handleAutoSave(params.id, selectedTemplate, getValues());
      setIsSubmittingForce(false);
    } else {
      setIsConflictWarning(false);
      removeTemplateFromLocalStorage(params.id);
      getActiveTemplate();
    }
  };

  const handleAddSection = (section) => {
    Object.assign(initialValues[section.component], {
      ...initialValues[section.component],
      name: section.name,
      hashId: uuidv4(),
      isCustom: true
    });
    addSection(initialValues[section.component]);
  };

  const btnRef = useRef();

  const parseResourceSrc = (resourceObj) =>
    resourceObj?.key
      ? `${REACT_APP_ASSET_CDN_URL}/${resourceObj.key}/${THUMBNAIL_TYPE.ANSWER_OPTION_ICON}`
      : "";

  const parseTemplateResourceSrc = (type) =>
    type
      ? `${REACT_APP_ASSET_CDN_URL}/customer-website/${type}/main.jpg/${THUMBNAIL_TYPE.ANSWER_OPTION_ICON}`
      : "";

  const parsePreviewResourceSrc = (resourceObj) =>
    resourceObj?.key ? `${REACT_APP_ASSET_CDN_URL}/${resourceObj.key}` : "";

  const parseTemplatePreviewResourceSrc = (type) =>
    type ? `${REACT_APP_ASSET_CDN_URL}/customer-website/${type}/main.jpg` : "";

  const handleSaveWebsite = async (formVals, isPublished) => {
    try {
      const {
        domain: updatedDomain,
        subDomain: updatedSubDomain,
        language = DEFAULT_WEBSITE_LANGUAGE,
        prompt,
        ...templateData
      } = formVals;
      const template = {
        templateType: selectedTemplate,
        active: true,
        isPublished: isPublished || false,
        oca: oca.current,
        language,
        data: templateData,
        isHubspotCustomer: isHubspotTabSelected,
        tabRoute
      };
      const response = await saveCustomerWebTemplate(
        params.id,
        !category ? template : { ...template, category: category?._id }
      );

      // Handle the API response
      const updatedTemplate = response.data.data;
      showSuccess(response.data.message);

      setActiveTemplate(updatedTemplate);
      resetForm(updatedTemplate?.data, reset);
      updatedTemplate?.isPublished &&
        setValue("domain", updatedTemplate.domain);
      removeTemplateFromLocalStorage(params.id);
      if (updatedTemplate && !updatedTemplate?.isPublished) {
        setIsDraftWarning(true);
        oca.current = updatedTemplate?.oca;
      } else {
        setIsDraftWarning(false);
        oca.current = 0;
      }
      return updatedTemplate;
    } catch (err) {
      showError(err?.response?.data?.message || "API Error");
      console.error("An error occurred while submitting the form:", err);
    }
  };

  const handleAutoSave = useCallback(
    async (customerId, templateType, formValues, isHubspotTabSelected) => {
      try {
        const {
          domain,
          subDomain,
          language = DEFAULT_WEBSITE_LANGUAGE,
          prompt,
          ...templateData
        } = formValues;

        const template = {
          templateType: templateType,
          active: true,
          isPublished: false,
          oca: oca.current,
          language,
          data: templateData,
          isHubspotCustomer: isHubspotTabSelected,
          tabRoute
        };
        const response = await saveCustomerWebTemplate(
          customerId,
          !category ? template : { ...template, category: category?._id }
        );
        const updatedTemplate = response.data.data;
        setActiveTemplate(updatedTemplate);
        setIsDraftWarning(true);
        setIsConflictWarning(false);
        oca.current = updatedTemplate?.oca;
        removeTemplateFromLocalStorage(customerId);
      } catch (err) {
        console.log(err);
        const tempData = {
          templateType: templateType,
          oca: oca.current,
          data: formValues
        };
        const storedData = localStorage.getItem("customerSite");
        try {
          const parsedData = storedData ? JSON.parse(storedData) : {};
          parsedData[customerId] = tempData;
          localStorage.setItem("customerSite", JSON.stringify(parsedData));
        } catch (err) {
          console.log(err);
        }
        if (err?.response?.status === 409) {
          setIsConflictWarning(true);
          setIsDraftWarning(false);
        } else {
          showError(err?.response?.data?.message || "Auto Save Issue");
        }
        console.error("An error occurred while submitting the form:", err);
      }
    },
    [category, tabRoute]
  );

  const handlePreview = async () => {
    let templateId = activeTemplate._id;
    try {
      setIsPreviewing(true);
      const response = await getCustomerActiveWebsiteTemplate({
        customerId: params.id,
        tabRoute
      });

      const template = response.data.data;
      if (template) {
        if (!template?.isPublished && template?.oca !== oca.current) {
          oca.current = template?.oca;
          toggleForcePushDialog();
          return;
        }
      }

      if (
        isDirty ||
        findTemplateFromLocalStorage(params.id) ||
        template.templateType !== selectedTemplate
      ) {
        const template = await handleSaveWebsite(getValues(), false);
        templateId = template?._id;
      }

      const url = generateCustomerSitePreviewUrl({ templateId, tabRoute });
      window.open(url, "_blank");
    } catch (error) {
      showError(error?.response?.message);
      console.error(error?.response?.message || error);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleDeleteDraft = async () => {
    setIsDeletingDraft(true);
    try {
      await deleteTemplateDraft({
        customerId: params.id,
        tabRoute
      });
      removeTemplateFromLocalStorage(params.id);
      setIsDraftWarning(false);
      getActiveTemplate();
    } catch (err) {
      showError(err?.response?.data?.message || "Delete Draft Error");
    }
    setIsDeletingDraft(false);
  };

  const onSubmit = async (formVals) => {
    try {
      setIsSubmitting(true);
      await handleSaveWebsite(formVals, true);
    } catch (error) {
      console.error("An error occurred while submitting the form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to handle template render
  const resetForm = (data, reset) => {
    isFormResetting.current = true;
    reset(data);
    requestAnimationFrame(() => {
      isFormResetting.current = false;
    });
  };

  const getDNS = async (domain, isValid) => {
    try {
      if (!isValid) {
        return;
      }
      setDnsLoading(true);
      setShowCheckDNS(false);
      const dnsRecord = await getDnsRecord({ domain });
      const dnsResult = dnsRecord.data.data.dnsResult;
      setDnsResult(dnsResult);
      setDnsLoading(false);
      setIsEditable(false);
    } catch (err) {
      console.log(err);
      setDnsLoading(false);
      setDnsResult(
        err?.response?.data?.data?.dnsResult || DNS_RECORD_TYPES.NOT_FOUND
      );
      setIsEditable(true);
    }
  };

  const getActiveTemplate = async () => {
    setIsAllowedChange(false);
    try {
      const response = await getCustomerActiveWebsiteTemplate({
        customerId: params.id,
        tabRoute
      });

      const template = response.data.data;
      setCategory(template?.category);
      setActiveTemplate(template);

      const existingRecord = findTemplateFromLocalStorage(params.id);
      if (existingRecord) {
        resetForm(
          {
            ...existingRecord.data,
            language: existingRecord.data?.language || DEFAULT_WEBSITE_LANGUAGE
          },
          reset
        );
        oca.current = existingRecord.oca;
        setSelectedTemplate(existingRecord.templateType);
        const sectionKeys = existingRecord.data.sections.map((_, index) =>
          index.toString()
        );
        setSectionActiveKeys(sectionKeys);
      } else {
        const template = response.data.data;
        if (template) {
          if (template?.isPublished) {
            setIsDraftWarning(false);
          } else {
            setIsDraftWarning(true);
            oca.current = template.oca;
          }
          setSelectedTemplate(template.templateType ?? DEFAULT_WEB_TEMP);
          if (template.data) {
            template.data.navbar?.links?.links?.forEach((link) => {
              if (link.link) {
                link.link.name = t(
                  `customerWebsite.sections.${link.link?.component}`
                );
              }
            });
            template.data.footer.footer_links.quick_links.links?.forEach(
              (link) => {
                if (link.link) {
                  link.link.name = t(
                    `customerWebsite.sections.${link.link?.component}`
                  );
                }
              }
            );
            resetForm(
              {
                ...template.data,
                language: template.language || DEFAULT_WEBSITE_LANGUAGE
              },
              reset
            );
            const sectionKeys = template.data.sections.map((_, index) =>
              index.toString()
            );
            setSectionActiveKeys(sectionKeys);
          } else {
            resetForm(
              { ...defaultValues, language: DEFAULT_WEBSITE_LANGUAGE },
              reset
            );
          }
        } else {
          setIsEditable(true);
          setDnsResult(null);
          resetForm(
            { ...defaultValues, language: DEFAULT_WEBSITE_LANGUAGE },
            reset
          );
        }
        template?.isPublished && setValue("domain", template.domain);
      }
    } catch (error) {
      console.error("Error fetching template:", error);
    } finally {
      setTimeout(() => {
        setIsAllowedChange(true);
      }, 1000);
    }
  };

  const getActiveWebsiteDomain = async () => {
    try {
      const response = await getWebsiteDomainByCustomerId(params.id);
      const domain = response?.data?.data?.domain;
      if (domain) {
        setValue("domain", domain);
        const isValid = await trigger("domain");
        getDNS(domain, isValid);
      } else {
        setValue("domain", "");
        setIsEditable(true);
        setDnsResult(null);
      }
    } catch (error) {
      console.error("Error fetching domain:", error);
    }
  };

  const getAllTemplates = async () => {
    try {
      const response = await getCustomerAllWebTemplates();
      const templates = response.data.data;
      setTemplates(templates);
    } catch (error) {
      console.error("Error fetching template:", error);
    }
  };

  const activeWebsiteDomainCB = useCallback(
    getActiveWebsiteDomain,
    // eslint-disable-next-line
    [params.id, setValue, trigger]
  );

  const allTemplatesCB = useCallback(getAllTemplates, []);

  useEffect(() => {
    allTemplatesCB();
  }, [allTemplatesCB]);

  useEffect(() => {
    (async () => {
      await getActiveTemplate();
      await activeWebsiteDomainCB();
    })();
    // Keep template loading tied to the selected website, not translation changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, tabRoute, activeWebsiteDomainCB]);

  useEffect(() => {
    setMainLinks(
      sections.map(({ component, hashId, name, isCustom }) => ({
        name: isCustom ? name : t(`customerWebsite.sections.${component}`),
        component,
        hashId,
        isCustom
      }))
    );
  }, [sections, t]);

  const renderSection = (section, index) => {
    switch (section.component) {
      case "hero":
        return (
          <HeroSection
            index={index}
            parseResourceSrc={parseResourceSrc}
            parsePreviewResourceSrc={parsePreviewResourceSrc}
            isFormChanged={isFormChanged}
            templateType={selectedTemplate}
          />
        );
      case "about":
        return (
          <AboutSection
            index={index}
            parseResourceSrc={parseResourceSrc}
            parsePreviewResourceSrc={parsePreviewResourceSrc}
            isFormChanged={isFormChanged}
            templateType={selectedTemplate}
          />
        );
      case "service":
        return (
          <ServiceSection
            index={index}
            parseResourceSrc={parseResourceSrc}
            parsePreviewResourceSrc={parsePreviewResourceSrc}
            isFormChanged={isFormChanged}
          />
        );
      case "banner":
        return (
          <FirstBanner
            index={index}
            parseResourceSrc={parseResourceSrc}
            parsePreviewResourceSrc={parsePreviewResourceSrc}
            isFormChanged={isFormChanged}
          />
        );
      case "keyPoint":
        return <KeyPointSection index={index} isFormChanged={isFormChanged} />;
      case "secondBanner":
        return (
          <SecondBanner
            index={index}
            parseResourceSrc={parseResourceSrc}
            parsePreviewResourceSrc={parsePreviewResourceSrc}
            isFormChanged={isFormChanged}
            templateType={selectedTemplate}
          />
        );
      case "map":
        return <MapSection index={index} />;
      case "faq":
        return <FaqSection index={index} isFormChanged={isFormChanged} />;
      default:
        return null;
    }
  };

  const toggleAccordion = (key) => {
    const isFound = sectionActiveKeys.find((i) => i === key);
    if (isFound) {
      const activeKeys = [...sectionActiveKeys.filter((i) => i !== key)];
      setSectionActiveKeys(activeKeys);
    } else {
      const activeKeys = [...sectionActiveKeys];
      activeKeys.push(key);
      setSectionActiveKeys(activeKeys);
    }
  };

  const removeTemplateFromLocalStorage = (customerId) => {
    const storedData = localStorage.getItem("customerSite");
    const parsedData = storedData ? JSON.parse(storedData) : {};
    delete parsedData[customerId];
    localStorage.setItem("customerSite", JSON.stringify(parsedData));
  };

  const findTemplateFromLocalStorage = (customerId) => {
    const storedData = localStorage.getItem("customerSite");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      return parsedData[customerId];
    }
  };

  useEffect(() => {
    clearInterval(apiIntervalHandler.current);
    apiIntervalHandler.current = setInterval(() => {
      if (isFormChanged.current) {
        isFormChanged.current = false;
        handleAutoSave(
          params.id,
          selectedTemplate,
          getValues(),
          isHubspotTabSelected
        );
      }
    }, 1000);
    return () => clearInterval(apiIntervalHandler.current);
  }, [
    params.id,
    selectedTemplate,
    getValues,
    handleAutoSave,
    isHubspotTabSelected
  ]);

  useEffect(() => {
    if (!customer && params.id) {
      const getCustomer = async () => {
        try {
          const response = await getCustomerById(params.id);
          setCustomer(response.data?.data);
        } catch (err) {
          console.log(err);
          showError(
            err.response.data.message ||
              t("toastMessage.errorInGetting", {
                what: t("common.customer")
              })
          );
        }
      };
      getCustomer();
    }
    // eslint-disable-next-line
  }, [customer, params.id]);

  watch(async (data, state) => {
    if (isFormResetting.current) return;
    if (state.type !== "change") return;
    if (state.name === "domain" || state.name === "prompt") return;
    isFormChanged.current = true;
  });

  const findImageData = (name, websiteImages) => {
    const found = websiteImages.find((item) => item.name === name);
    return found?.data || [];
  };

  const setImageField = ({
    websiteImages,
    parsedContent,
    fieldConfigs = []
  }) => {
    const defaultImage = {
      key: "",
      url: "",
      _id: "",
      asset: ""
    };

    fieldConfigs.forEach(
      ({ field, isArray = false, mapInside = null }, index) => {
        const imageData = findImageData(
          parsedContent[index].name,
          websiteImages
        );

        if (mapInside) {
          parsedContent[index].data[mapInside] = parsedContent[index].data[
            mapInside
          ].map((item, idx) => ({
            ...item,
            image: imageData[idx] || defaultImage
          }));
        } else if (isArray) {
          parsedContent[index].data[field] = imageData.length
            ? imageData
            : new Array(3).fill(defaultImage);
        } else {
          parsedContent[index].data[field] = imageData[0] || defaultImage;
        }
      }
    );
  };

  const generateContent = async (formVals) => {
    try {
      await updateCustomer(params.id, {
        phone: formVals.phone,
        address: formVals.address
      });
      const requiredImagesData = extractImageRequirements(sections);
      formVals.requiredImagesData = requiredImagesData;
      formVals.customerId = params.id;
      const response = await generateWebsiteContent(formVals);
      const imagesData = await generateWebsiteImages({
        formVals,
        requiredImagesData
      });
      const AISections = response.data.data;
      const images = imagesData.data.data;
      setCategory(formVals.category);

      setImageField({
        websiteImages: images,
        parsedContent: AISections,
        fieldConfigs: [
          {
            field: "image",
            isArray: false,
            mapInside: null
          },
          {
            field: "images",
            isArray: true,
            mapInside: null
          },
          {
            field: "services",
            mapInside: "services",
            isArray: true
          },
          {
            field: "image",
            isArray: false,
            mapInside: null
          },
          {
            field: "image",
            isArray: false,
            mapInside: null
          }
        ]
      });

      AISections.push(initialValues.map);

      replaceSection(AISections);

      // sections.forEach((section, index) => {
      //   const data = response.data.data.find(
      //     (s) => s.component === section.component
      //   );
      //   const images = imagesData.data.data.find(
      //     (s) => s.name === section.name
      //   );
      //   if (data && section.data.active !== false) {
      //     setValue(`sections[${index}].data.heading.text`, data.data.heading);
      //     setValue(`sections[${index}].data.para.text`, data.data.para);
      //     const services = section?.data?.services;
      //     const keyPoints = section?.data?.keyPoints;
      //     switch (section.component) {
      //       case "about":
      //         if (images) {
      //           setValue(`sections[${index}].data.images`, images.data);
      //         }
      //         break;
      //       case "hero":
      //         if (images) {
      //           setValue(`sections[${index}].data.image`, images.data[0]);
      //         }
      //         setValue(
      //           `sections[${index}].data.bullets.data`,
      //           data.data.bullets
      //         );
      //         setValue(`sections[${index}].data.btn.text`, data.data.btn.text);
      //         break;
      //       case "service":
      //         services.forEach((service, i) => {
      //           if (images) {
      //             setValue(
      //               `sections[${index}].data.services[${i}].image`,
      //               images.data[i]
      //             );
      //           }
      //           setValue(
      //             `sections[${index}].data.services[${i}].heading.text`,
      //             data.data.services[i].heading
      //           );
      //           setValue(
      //             `sections[${index}].data.services[${i}].heading.text`,
      //             data.data.services[i].heading
      //           );
      //           setValue(
      //             `sections[${index}].data.services[${i}].para.text`,
      //             data.data.services[i].para
      //           );
      //         });
      //         break;
      //       case "banner":
      //         if (images) {
      //           setValue(`sections[${index}].data.bg_image`, images.data[0]);
      //         }
      //         setValue(`sections[${index}].data.btn.text`, data.data.btn.text);
      //         break;
      //       case "keyPoint":
      //         keyPoints.forEach((keyPoint, i) => {
      //           setValue(
      //             `sections[${index}].data.keyPoints[${i}].heading.text`,
      //             data.data.keyPoints[i].heading
      //           );
      //           setValue(
      //             `sections[${index}].data.keyPoints[${i}].para.text`,
      //             data.data.keyPoints[i].para
      //           );
      //         });
      //         break;
      //       case "secondBanner":
      //         if (images) {
      //           setValue(`sections[${index}].data.image`, images.data[0]);
      //         }
      //         setValue(`sections[${index}].data.btn.text`, data.data.btn.text);
      //         break;
      //       case "faq":
      //         setValue(`sections[${index}].data.faqs.data`, data.data.faqs);
      //         break;
      //       default:
      //         break;
      //     }
      //   }
      // });

      const links = [
        {
          text: "Über uns",
          link: {
            name: t(`customerWebsite.sections.about`),
            component: "about",
            hashId: "about",
            isCustom: false
          }
        },
        {
          text: "Leistungen",
          link: {
            name: t(`customerWebsite.sections.service`),
            component: "service",
            hashId: "service",
            isCustom: false
          }
        },
        {
          text: "Kernpunkte",
          link: {
            name: t(`customerWebsite.sections.keyPoint`),
            component: "keyPoint",
            hashId: "keyPoint",
            isCustom: false
          }
        },
        {
          text: "Anfrage",
          link: {
            name: t(`customerWebsite.sections.secondBanner`),
            component: "secondBanner",
            hashId: "secondBanner",
            isCustom: false
          }
        }
      ];

      links.forEach((link, index) => {
        setValue(`navbar.links.links[${index}].text`, link.text);
        setValue(`navbar.links.links[${index}].link`, link.link);
        setValue(
          `footer.footer_links.quick_links.links[${index}].text`,
          link.text
        );
        setValue(
          `footer.footer_links.quick_links.links[${index}].link`,
          link.link
        );
      });

      setValue("navbar.phone.text", formVals.phone);
      setValue("navbar.address.text", formVals.address);
      setValue("footer.contact_card.btn.text", "Jetzt kontaktieren");

      isFormChanged.current = true;
    } catch (err) {
      console.error(err);
    }
  };

  const handleMouseDown = (e) => {
    startPosRef.current = { x: e.clientX, y: e.clientY };
    allowClickRef.current = true;

    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startPosRef.current.x;
      const dy = moveEvent.clientY - startPosRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > threshold) {
        allowClickRef.current = false;
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleAccordionButtonClick = (e, key) => {
    if (!allowClickRef.current) {
      e.stopPropagation(); // Prevent toggle
      e.preventDefault();
      return;
    }
    // allow toggle
    toggleAccordion(key?.toString());
  };

  return (
    <div className="my-2">
      <WebsiteInfo className="sticky-top mb-3">
        <div className="d-flex align-items-center justify-content-between py-2">
          <PageHeading className="mt-0">{t("common.website")}</PageHeading>
          <div>
            <Button
              onClick={toggleAIPromptDialog}
              className="me-3"
              style={{ paddingBottom: 2, paddingTop: 0 }}
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} />{" "}
              <span className="ms-2">Write with AI</span>
            </Button>
            <Button
              variant="secondary"
              className="me-3"
              type="button"
              style={{ paddingBottom: 2, paddingTop: 0 }}
              onClick={toggleDuplicateDialog}
              disabled={!activeTemplate._id || !activeTemplate.isPublished}
            >
              <FontAwesomeIcon icon={faClone} />
              <span className="pe-2 ms-2">
                {t("customerWebsite.duplicate")}
              </span>
            </Button>
            <Button
              variant="dark"
              className="me-3"
              type="button"
              style={{ paddingBottom: 2, paddingTop: 0 }}
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
              disabled={isSubmitting || isConflictWarning}
            >
              {isSubmitting ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <FontAwesomeIcon icon={faUpload} />
              )}

              <span className="pe-2 ms-2">Publish</span>
            </Button>
            <Button
              variant="success"
              type="button"
              style={{ paddingBottom: 2, paddingTop: 0 }}
              onClick={handlePreview}
              className="me-3"
              disabled={
                isPreviewing ||
                isConflictWarning ||
                (!activeTemplate && !isDirty)
              }
            >
              <span className="pe-2 me-2">Preview</span>
              {isPreviewing ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <FontAwesomeIcon icon={faEye} />
              )}
            </Button>
            {activeTemplate && !activeTemplate.isPublished && (
              <Button
                variant="danger"
                type="button"
                style={{ paddingBottom: 2, paddingTop: 0 }}
                disabled={isDeletingDraft}
                onClick={handleDeleteDraft}
              >
                <span className="pe-2 me-2">Discard Draft</span>
                {isDeletingDraft ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <FontAwesomeIcon icon={faTrash} />
                )}
              </Button>
            )}
          </div>
        </div>
        {isDraftWarning && (
          <div className="bg-warning text-dark w-100 mt-1 mb-3 p-1 px-2 rounded d-flex align-items-center">
            <p className="mb-0">There are some changes are not published yet</p>
          </div>
        )}
        {isConflictWarning && (
          <div className="bg-warning text-dark w-100 mt-1 mb-3 p-2 px-2 rounded d-flex align-items-center justify-content-between">
            <p className="mb-0 me-2">Someone has worked on this template</p>
            <div>
              <Button
                variant="dark"
                size="sm"
                className="me-2 rounded"
                onClick={() => forcePushResponseAction(false)}
              >
                Discard Changes
              </Button>
              <Button
                variant="danger"
                size="sm"
                className="rounded"
                disabled={isSubmittingForce}
                onClick={() => forcePushResponseAction(true)}
              >
                {isSubmittingForce && (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                <span className="ms-2">Force Push</span>
              </Button>
            </div>
          </div>
        )}
      </WebsiteInfo>
      <FormProvider {...methods}>
        <PreventReload />
        <form>
          <CustomCard className="mb-3">
            <FieldSet>
              <WebsiteDomainSection
                btnRef={btnRef}
                isDomainValid={isDomainValid}
                setIsDomainValid={setIsDomainValid}
                dnsResult={dnsResult}
                dnsLoading={dnsLoading}
                getDNS={getDNS}
                showCheckDNS={showCheckDNS}
                setShowCheckDNS={setShowCheckDNS}
                isEditable={isEditable}
                setIsEditable={setIsEditable}
                tabRoute={tabRoute}
                // setShouldUpdateNonActive={setShouldUpdateNonActive}
              />
            </FieldSet>
          </CustomCard>
          <CustomCard className="mb-3">
            <FieldSet>
              <Row>
                <Col md={12}>
                  <Legend>
                    <h4 className="mb-0 fw-bold">
                      {t("customerWebsite.template")}
                    </h4>
                  </Legend>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={12}>
                  <div className="d-flex" style={{ gap: "20px" }}>
                    {templates.length > 0 &&
                      templates.map((template, index) => {
                        return (
                          <div
                            key={template.type}
                            className="position-relative rounded cursor-pointer"
                            style={{
                              border: `5px solid ${
                                selectedTemplate === template.type
                                  ? "blue"
                                  : "black"
                              }`
                            }}
                            onMouseEnter={() => setHoverActive(index)}
                            onClick={() => {
                              setSelectedTemplate(template.type);
                              isFormChanged.current = true;
                            }}
                            onMouseLeave={() => setHoverActive(-1)}
                          >
                            <Img
                              className="rounded cursor-pointer"
                              src={parseTemplateResourceSrc(template.type)}
                            />
                            {hoverActive === index && (
                              <div
                                className="cursor-pointer position-absolute top-0 left-0 d-flex justify-content-center align-items-center"
                                style={{
                                  width: "150px",
                                  height: "300px",
                                  background: "rgba(0,0,0,0.3)"
                                }}
                              >
                                <Button
                                  variant="outline-light"
                                  size="sm"
                                  onClick={() => setShowPreviewTemplate(index)}
                                >
                                  {t("customerWebsite.preview")}
                                </Button>
                              </div>
                            )}
                            {showPreviewTemplate === index && (
                              <ImagePreviewModal
                                source={parseTemplatePreviewResourceSrc(
                                  template.type
                                )}
                                toggleModal={() => setShowPreviewTemplate(-1)}
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </Col>
              </Row>
            </FieldSet>
          </CustomCard>
          <BrandColorsSection
            isAllowedChange={isAllowedChange}
            isFormChanged={isFormChanged}
          />
          <CustomCard className="mb-3">
            <FieldSet>
              <Row>
                <Col md={12}>
                  <Legend>
                    <h4 className="mb-0 fw-bold">Language</h4>
                  </Legend>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col xs={12} md={4}>
                  <Form.Label>Website language</Form.Label>
                  <Form.Select
                    {...register("language")}
                    className={errors.language && "border-danger"}
                  >
                    {WEBSITE_LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.language && (
                    <p className="text-danger mb-0 mt-1 small">
                      {errors.language.message}
                    </p>
                  )}
                </Col>
              </Row>
            </FieldSet>
          </CustomCard>
          <HeaderSection
            logoTypes={logoTypes}
            sections={mainLinks}
            parseResourceSrc={parseResourceSrc}
            parsePreviewResourceSrc={parseResourceSrc}
            isFormChanged={isFormChanged}
            customerData={customer}
          />
          <CustomCard className="mb-3 bg-transparent border-0 px-0">
            <Row>
              <Col md={12} className="text-end">
                <Button
                  className="btn-primary w-full btn-sm"
                  onClick={handleCollapseSections}
                >
                  Toggle Collapse
                </Button>
              </Col>
            </Row>
          </CustomCard>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="sections">
              {(provided) => {
                return (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    <Accordion
                      flush
                      activeKey={sectionActiveKeys}
                      className="mb-3"
                      alwaysOpen
                    >
                      {sections.map((section, index) => (
                        <Draggable
                          key={section.id}
                          draggableId={section.id}
                          index={index}
                        >
                          {(provided) => {
                            return (
                              <Accordion.Item
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                key={section._id}
                                eventKey={index.toString()}
                                className="mb-3 border"
                              >
                                {/* Handling this div as accordion button */}
                                <div
                                  {...provided.dragHandleProps}
                                  className="d-flex bg-white align-items-center px-3"
                                  onMouseDown={handleMouseDown}
                                  onClick={(e) =>
                                    handleAccordionButtonClick(e, index)
                                  }
                                >
                                  <FontAwesomeIcon
                                    className="fw-regular text-secondary"
                                    size="lg"
                                    icon={faArrowsUpDown}
                                  />
                                  <div
                                    className="p-3 bg-white rounded border-0 box-shadow-0"
                                    style={{ boxShadow: "none" }}
                                  >
                                    <div
                                      className="d-flex align-items-center"
                                      style={{ gap: "10px" }}
                                    >
                                      <Legend>
                                        <h4 className="mb-0 fw-bold text-black">
                                          {section.isCustom
                                            ? section.name
                                            : t(
                                                `customerWebsite.sections.${section.component}`
                                              )}

                                          <span
                                            className="ms-2 text-secondary"
                                            style={{ fontSize: "16px" }}
                                          >
                                            (
                                            {t(
                                              `customerWebsite.sections.${section.component}`
                                            )}
                                            )
                                          </span>
                                        </h4>
                                      </Legend>
                                      {section.component === "map" && (
                                        <p
                                          className="text-primary fw-bold mb-0"
                                          style={{ fontSize: "14px" }}
                                        >
                                          ({t("customerWebsite.mapMessage")})
                                        </p>
                                      )}
                                      {section.component === "faq" && (
                                        <OverlayTrigger
                                          overlay={
                                            <Tooltip>
                                              {watch(
                                                `sections[${index}].data.active`
                                              )
                                                ? "Display On"
                                                : "Display Off"}
                                            </Tooltip>
                                          }
                                        >
                                          <Form.Check
                                            role="button"
                                            type="switch"
                                            {...register(
                                              `sections[${index}].data.active`
                                            )}
                                          />
                                        </OverlayTrigger>
                                      )}
                                    </div>
                                  </div>
                                  {section.isCustom && (
                                    <Button
                                      onClick={() => {
                                        setDependentStates({ index });
                                        toggleConfirmationDialog();
                                      }}
                                      variant="outline-danger mx-2"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  )}
                                </div>
                                {(section.component !== "faq" ||
                                  watch(`sections[${index}].data.active`)) && (
                                  <AccordionBody className={`bg-white`}>
                                    {renderSection(section, index)}
                                  </AccordionBody>
                                )}
                              </Accordion.Item>
                            );
                          }}
                        </Draggable>
                      ))}
                    </Accordion>
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
          <AddNewSection
            addSection={handleAddSection}
            selectedTemplate={selectedTemplate}
          />
          <FooterSection
            logoTypes={logoTypes}
            parseResourceSrc={parseResourceSrc}
            parsePreviewResourceSrc={parseResourceSrc}
            sections={mainLinks}
            isFormChanged={isFormChanged}
            customerData={customer}
          />
        </form>
      </FormProvider>
    </div>
  );
};
export default CustomerWebsite;
